import { action, makeObservable, observable } from "mobx";
import {
  DECISION_FLOW_CONFIG,
  getNextQuestionId,
} from "components/AbnSoleTraderRegisterForm/components/DecisionStep/decisionFlowConfig";
import { SoleTraderDecisionAnswerEntry } from "services/company/companyTypes";
import { type IssuesStore } from "services/issues/issuesStore";
import { QuestionStoreError, QuestionsStore } from "services/questions/questionsStore";
import { DirectionType } from "services/registerForm/registerFormStepsStoreBase";
import { type AnswerTrailEntry, SoleTraderDecisionQuestion } from "./soleTraderDecisionConstants";

export class SoleTraderDecisionQuestionsStore extends QuestionsStore<SoleTraderDecisionQuestion> {
  private _answerTrail: AnswerTrailEntry[] = [];
  private _isFlowComplete = false;

  constructor(protected readonly _issuesStore: IssuesStore) {
    super(SoleTraderDecisionQuestion, _issuesStore);

    makeObservable(this, {
      _answerTrail: observable,
      _isFlowComplete: observable,
      addToAnswerTrail: action,
      clearAnswerTrail: action,
      initializeFromData: action,
      processAnswer: action,
      goNext: action,
      goBack: action,
      goToRelevantQuestion: action,
    } as any);
  }

  get answerTrail(): AnswerTrailEntry[] {
    return this._answerTrail;
  }

  get isFirstQuestion(): boolean {
    return this.activeQuestionKey === DECISION_FLOW_CONFIG.firstQuestionId;
  }

  addToAnswerTrail(entry: AnswerTrailEntry): void {
    // Remove any entries after the current question (in case user went back)
    const currentIndex = this._answerTrail.findIndex((e) => e.questionId === entry.questionId);
    if (currentIndex >= 0) {
      this._answerTrail = this._answerTrail.slice(0, currentIndex);
    }
    this._answerTrail = [...this._answerTrail, entry];
  }

  clearAnswerTrail(): void {
    this._answerTrail = [];
  }

  getNextQuestionId(questionId: string, answerId: string): string {
    return getNextQuestionId(questionId, answerId);
  }

  isFlowComplete(nextQuestionId: string): boolean {
    return nextQuestionId === "0";
  }

  getAnswerTrailForApi(): SoleTraderDecisionAnswerEntry[] {
    return this._answerTrail.map(
      (entry) =>
        new SoleTraderDecisionAnswerEntry({
          questionId: entry.questionId,
          answerId: entry.answerId,
        }),
    );
  }

  processAnswer(answerId: string): { isFlowComplete: boolean } {
    const questionId = this.activeQuestionKey;
    const nextQuestionId = this.getNextQuestionId(questionId, answerId);

    this.addToAnswerTrail({
      questionId,
      answerId,
      nextQuestionId,
    });

    if (this.isFlowComplete(nextQuestionId)) {
      this._isFlowComplete = true;
      return { isFlowComplete: true };
    }

    this._isFlowComplete = false;
    this.goToQuestion(nextQuestionId as SoleTraderDecisionQuestion);
    return { isFlowComplete: false };
  }

  goNext(): void {
    // When flow is complete, throw to signal that step should advance
    if (this._isFlowComplete) {
      throw new QuestionStoreError("Decision flow is complete");
    }
    // Otherwise, navigation was already handled by processAnswer - do nothing
  }

  goBack(): void {
    // Use answer trail to navigate back instead of array index order
    const currentQuestionId = this.activeQuestionKey;

    // Find the entry that led to the current question
    const previousEntry = this._answerTrail.find((entry) => entry.nextQuestionId === currentQuestionId);

    if (previousEntry) {
      // Go back to the question that led us here
      // Keep the entry in the trail so the answer is preserved when navigating back
      this.goToQuestion(previousEntry.questionId as SoleTraderDecisionQuestion);
    } else if (this.isFirstQuestion) {
      // Already at first question, throw error to signal step should go back
      throw new QuestionStoreError("Can't go back from the first question");
    } else {
      // Fallback to first question if no trail entry found
      this.goToQuestion(DECISION_FLOW_CONFIG.firstQuestionId as SoleTraderDecisionQuestion);
    }
  }

  goToRelevantQuestion(direction: DirectionType = DirectionType.FORWARD): void {
    const lastEntry = this._answerTrail[this._answerTrail.length - 1];
    if (direction === DirectionType.BACKWARD && this._isFlowComplete && lastEntry) {
      this.goToQuestion(lastEntry.questionId as SoleTraderDecisionQuestion);
      return;
    }

    super.goToRelevantQuestion(direction);
  }

  initializeFromData(data: SoleTraderDecisionAnswerEntry[] | null | undefined): void {
    if (!data || data.length === 0) {
      return;
    }
    // Convert API format (without nextQuestionId) to internal format (with nextQuestionId)
    const answerTrail: AnswerTrailEntry[] = [];
    for (const entry of data) {
      const nextQuestionId = this.getNextQuestionId(entry.questionId, entry.answerId);
      answerTrail.push({
        questionId: entry.questionId,
        answerId: entry.answerId,
        nextQuestionId,
      });
    }
    this._answerTrail = answerTrail;

    // Set flow complete status based on last entry
    const lastEntry = answerTrail[answerTrail.length - 1];
    if (lastEntry) {
      this._isFlowComplete = this.isFlowComplete(lastEntry.nextQuestionId);

      // Navigate to the appropriate question
      if (this._isFlowComplete) {
        // Stay on the last answered question if flow is complete
        this.goToQuestion(lastEntry.questionId as SoleTraderDecisionQuestion);
      } else {
        // Go to the next unanswered question
        this.goToQuestion(lastEntry.nextQuestionId as SoleTraderDecisionQuestion);
      }
    }
  }

  get flowComplete(): boolean {
    return this._isFlowComplete;
  }

  getAnswerForQuestion(questionId: string): string | undefined {
    const entry = this._answerTrail.find((e) => e.questionId === questionId);
    return entry?.answerId;
  }

  protected _getRelevantQuestion(): SoleTraderDecisionQuestion | undefined {
    // Start with the first question from config
    return DECISION_FLOW_CONFIG.firstQuestionId as SoleTraderDecisionQuestion;
  }

  protected _shouldSkipQuestion(question: SoleTraderDecisionQuestion): boolean {
    // Skip questions that are not in the answer trail (user hasn't visited them)
    // The first question should never be skipped
    if (question === DECISION_FLOW_CONFIG.firstQuestionId) {
      return false;
    }

    // Skip if question is not in the answer trail
    const isInAnswerTrail = this._answerTrail.some((entry) => entry.questionId === question);
    return !isInAnswerTrail;
  }
}
